import React, { Fragment } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Avatar,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  makeStyles,
} from '@material-ui/core';
import {
  Flag as FlagIcon,
  Person as PersonIcon,
  OutlinedFlag as OutlinedFlagIcon,
  PlayArrow as PlayArrowIcon,
} from '@material-ui/icons';
import { Helmet } from 'react-helmet-async';
import moment from 'moment';
import _ from 'lodash';
import humanizeDuration from 'humanize-duration';
import {
  RetrospectiveTypeIcon,
  LocationTypeIcon,
} from '../../../data/constants';
import { ExportPicker } from '../../controls';
import {
  getVehiclePolls,
  shortPersonHeaders,
  shortVehicleHeaders,
  telematicsBoxPollHeaders,
} from '../../../apis/utilities';

const { useReducedResourceInformation } = window.config;

const useStyles = makeStyles((theme) => ({
  card: {
    margin: theme.spacing(1, 0.5, 1, 1),
  },
  vehicleTable: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  driverItem: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  timelineAvatar: {
    margin: -3,
    marginRight: 2,
    width: 30,
    height: 30,
  },
  timelineIcon: {
    fontSize: 16,
  },
}));

export default function VehicleVisit({
  color,
  background,
  identifier,
  driver,
  vehicle,
  visitLocation,
  startTime,
  endTime,
  distanceKilometres,
  maxSpeedKilometresPerHour,
}) {
  const history = useHistory();
  const classes = useStyles();

  function handleViewClick() {
    history.push(`/replay/intersections/${identifier}`);
  }

  async function fetchDataToConvert() {
    const polls = await getVehiclePolls(
      vehicle.telematicsBoxImei,
      startTime,
      endTime
    );

    const data = polls.map((poll) => ({
      ...poll,
      ...(useReducedResourceInformation
        ? { staffId: driver.code, type: vehicle.type }
        : {
            name: driver.forenames + ' ' + driver.surname,
            collarNumber: driver.collarNumber,
            personRole: driver.role,
          }),
      vehicleRole: vehicle.role,
      identificationNumber: vehicle.identificationNumber,
      registrationNumber: vehicle.registrationNumber,
      fleetNumber: vehicle.fleetNumber,
      time: new Date(poll.time),
      longitude: poll.position ? poll.position.coordinates[0] : 0,
      latitude: poll.position ? poll.position.coordinates[1] : 0,
    }));

    return data;
  }

  const vehicleVisitHeaders = [
    ...shortPersonHeaders,
    ...shortVehicleHeaders,
    ...telematicsBoxPollHeaders,
  ];

  const cardHeaderButtons = (
    <div>
      <IconButton
        aria-label="replay"
        title="View Replay"
        onClick={handleViewClick}
      >
        <PlayArrowIcon />
      </IconButton>
      <ExportPicker
        title="Download Vehicle Visit"
        fetchDataToConvert={fetchDataToConvert}
        filename="Vehicle Visit"
        headers={vehicleVisitHeaders}
      />
    </div>
  );

  return (
    <Card className={classes.card}>
      <Helmet>
        <title>
          IR3 | Vehicle Visit |{' '}
          {useReducedResourceInformation
            ? vehicle.fleetNumber
            : vehicle.registrationNumber}
        </title>
      </Helmet>
      <CardHeader
        avatar={
          <Avatar
            style={{
              background,
              color,
            }}
            title="Vehicle Visit"
          >
            <RetrospectiveTypeIcon type="vehicleVisits" />
          </Avatar>
        }
        title={
          useReducedResourceInformation
            ? vehicle.fleetNumber
            : vehicle.registrationNumber
        }
        subheader={
          useReducedResourceInformation
            ? vehicle.identificationNumber
            : vehicle.fleetNumber
        }
        action={cardHeaderButtons}
      />
      <CardContent>
        <Typography variant="subtitle2" color="textSecondary">
          Vehicle
        </Typography>
        <Table size="small" className={classes.vehicleTable}>
          <TableBody>
            {!useReducedResourceInformation && (
              <TableRow>
                <TableCell>
                  <Typography variant="caption" color="textSecondary">
                    Role
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption">{vehicle.role}</Typography>
                </TableCell>
              </TableRow>
            )}
            <TableRow>
              <TableCell>
                <Typography variant="caption" color="textSecondary">
                  {useReducedResourceInformation
                    ? 'Home Location'
                    : 'Home Station'}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="caption">{vehicle.homeStation}</Typography>
              </TableCell>
            </TableRow>
            {(vehicle.areas || []).map((area, index) => (
              <TableRow key={index}>
                <TableCell>
                  <Typography variant="caption" color="textSecondary">
                    {_.startCase(area.type)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption">{area.name}</Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Typography variant="subtitle2" color="textSecondary">
          Location
        </Typography>
        <ListItem component="div" className={classes.driverItem}>
          <ListItemAvatar>
            <Avatar title={visitLocation.type}>
              <LocationTypeIcon type={visitLocation.type} />
            </Avatar>
          </ListItemAvatar>
          <ListItemText
            primary={visitLocation.name}
            secondary={visitLocation.type}
          />
        </ListItem>
        {(driver || {}).identificationReference && (
          <Fragment>
            <Typography variant="subtitle2" color="textSecondary">
              Driver
            </Typography>
            <ListItem component="div" className={classes.driverItem}>
              <ListItemAvatar>
                <Avatar>
                  <PersonIcon />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  useReducedResourceInformation
                    ? driver.code
                    : `${driver.forenames || ''}  ${driver.surname || ''}`
                }
                secondary={driver.collarNumber}
              />
            </ListItem>
          </Fragment>
        )}
        <Typography variant="subtitle2" color="textSecondary">
          Timeline
        </Typography>
        <Stepper orientation="vertical">
          <Step active>
            <StepLabel
              icon={
                <Avatar className={classes.timelineAvatar}>
                  <FlagIcon className={classes.timelineIcon} />
                </Avatar>
              }
              optional={
                <Typography variant="caption">
                  {moment(startTime).format('DD/MM/YYYY, HH:mm:ss')}
                </Typography>
              }
            >
              Start
            </StepLabel>
            <StepContent />
          </Step>
          <Step active>
            <StepLabel
              icon={
                <Avatar className={classes.timelineAvatar}>
                  <OutlinedFlagIcon className={classes.timelineIcon} />
                </Avatar>
              }
              optional={
                <Typography variant="caption">
                  {moment(endTime).format('DD/MM/YYYY, HH:mm:ss')}
                </Typography>
              }
            >
              End
            </StepLabel>
            <StepContent />
          </Step>
        </Stepper>
        <Typography variant="subtitle2" color="textSecondary">
          Metrics
        </Typography>
        <Table size="small" className={classes.vehicleTable}>
          <TableBody>
            <TableRow>
              <TableCell>
                <Typography variant="caption" color="textSecondary">
                  Distance
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="caption">
                  {`${_.round(distanceKilometres * 0.62137119, 2)} miles`}
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <Typography variant="caption" color="textSecondary">
                  Maximum Speed
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="caption">
                  {`${_.round(maxSpeedKilometresPerHour * 0.62137119, 2)} MPH`}
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <Typography variant="caption" color="textSecondary">
                  Duration
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="caption">
                  {humanizeDuration(moment(endTime).diff(moment(startTime)))}
                </Typography>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
